// AdminGuard — gate component for platform admin routes (/admin/*).
//
// Mounted as a layout route INSIDE AppLayout (router.tsx): an admin is a
// regular user with their own workspace and scope, so the shell (sidebar,
// topbar) stays mounted; only the platform boundary is gated here.
//
// Flow:
//   1. /auth/me still loading → centered <Spinner/> (viewport rules of the shell).
//   2. Not a platform admin → redirect to /dashboard: deep links and stale
//      bookmarks must not render admin content for regular users.
//   3. Admin → render nested routes via <Outlet />.
//
// The role source is /auth/me (`platformRole`), refreshed on login and
// workspace switches; the API re-checks it on every /admin request anyway
// (withPlatformAdmin), so this gate is UX, not the security boundary.

import type { ReactElement } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { Spinner } from '@govard/ui-primitives';

import { useIsPlatformAdmin, useScopeLoading } from '../scope/useCurrentScope.js';

export function AdminGuard(): ReactElement {
  const isLoading = useScopeLoading();
  const isPlatformAdmin = useIsPlatformAdmin();

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] w-full items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isPlatformAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
}
